import { usePageTitle } from "@/hooks/use-page-title";
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/use-auth";
import {
  MessageCircle,
  Trophy,
  Heart,
  ThumbsUp,
  Send,
  Plus,
  Users,
  Target,
  Award,
  Star,
  Calendar,
  CheckCircle,
  Clock,
  Eye,
  EyeOff,
  Flag,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PageAbout } from "@/components/page-about";

const postFormSchema = z.object({
  type: z.string().min(1, "Please select a type"),
  title: z.string().min(5, "Title must be at least 5 characters"),
  content: z.string().min(20, "Content must be at least 20 characters"),
  isAnonymous: z.boolean().default(false),
  authorDisplayName: z.string().optional(),
});

type PostFormValues = z.infer<typeof postFormSchema>;

interface CommunityPost {
  id: string;
  userId: string;
  type: string;
  title: string;
  content: string;
  language: string;
  isAnonymous: boolean;
  authorDisplayName?: string;
  status: string;
  likesCount: number;
  commentsCount: number;
  viewsCount: number;
  createdAt: string;
  author?: {
    firstName?: string;
    lastName?: string;
  };
}

interface Challenge {
  id: string;
  title: string;
  description: string;
  type: string;
  goalType: string;
  goalValue: number;
  goalUnit?: string;
  rewardAmount: number;
  rewardDescription?: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  maxParticipants?: number;
  currentParticipants: number;
  createdAt: string;
}

interface ChallengeProgress {
  id: string;
  challengeId: string;
  userId: string;
  currentValue: number;
  completed: boolean;
  completedAt?: string;
}

interface UserAchievement {
  id: string;
  achievementId: string;
  earnedAt: string;
  achievement: {
    name: string;
    description: string;
    iconUrl?: string;
    category: string;
    pointValue: number;
  };
}

const postTypes = [
  { value: "success_story", label: "Success Story", icon: Trophy, color: "text-green-600" },
  { value: "question", label: "Question", icon: MessageCircle, color: "text-blue-600" },
  { value: "support", label: "Support Request", icon: Heart, color: "text-red-600" },
  { value: "forum", label: "Discussion", icon: Users, color: "text-purple-600" },
];

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export default function CommunityPage() {
  usePageTitle("Community Hub", "Connect with your HealthWallet community, share stories, and support healthcare funding initiatives.");
  const { toast } = useToast();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isPostDialogOpen, setIsPostDialogOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState<CommunityPost | null>(null);

  const { data: posts, isLoading: postsLoading } = useQuery<CommunityPost[]>({
    queryKey: ["/api/community-posts"],
  });

  const { data: challenges, isLoading: challengesLoading } = useQuery<Challenge[]>({
    queryKey: ["/api/challenges"],
  });

  const { data: myProgress } = useQuery<ChallengeProgress[]>({
    queryKey: ["/api/challenges/my-progress"],
  });

  const { data: achievements, isLoading: achievementsLoading } = useQuery<UserAchievement[]>({
    queryKey: ["/api/achievements/earned"],
  });

  const createPostMutation = useMutation({
    mutationFn: async (data: PostFormValues) => {
      const response = await apiRequest("POST", "/api/community-posts", data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/community-posts"] });
      setIsPostDialogOpen(false);
      form.reset();
      toast({
        title: "Post Created",
        description: "Your post has been submitted for review.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create post",
        variant: "destructive",
      });
    },
  });

  const likePostMutation = useMutation({
    mutationFn: async (postId: string) => {
      const response = await apiRequest("POST", `/api/community-posts/${postId}/like`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/community-posts"] });
    },
  });

  const joinChallengeMutation = useMutation({
    mutationFn: async (challengeId: string) => {
      const response = await apiRequest("POST", `/api/challenges/${challengeId}/join`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/challenges"] });
      queryClient.invalidateQueries({ queryKey: ["/api/challenges/my-progress"] });
      toast({
        title: "Challenge Joined!",
        description: "Good luck! Track your progress here.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to join challenge",
        variant: "destructive",
      });
    },
  });

  const form = useForm<PostFormValues>({
    mode: 'onBlur',
    resolver: zodResolver(postFormSchema),
    defaultValues: {
      type: "",
      title: "",
      content: "",
      isAnonymous: false,
      authorDisplayName: "",
    },
  });

  const isAnonymous = form.watch("isAnonymous");

  const approvedPosts = posts?.filter(p => p.status === "approved") || [];
  const successStories = approvedPosts.filter(p => p.type === "success_story");
  const forumPosts = approvedPosts.filter(p => p.type !== "success_story");

  const activeChallenges = challenges?.filter(c => c.isActive) || [];

  const totalPoints = achievements?.reduce((sum, a) => sum + a.achievement.pointValue, 0) || 0;

  return (
    <div className="space-y-6">
      <PageAbout
        title="Community Hub"
        description="Connect with other HealthWallet users to share experiences, get support, and participate in community health challenges. Build a network of support for your healthcare journey."
        features={[
          "Share stories and connect with patients in similar situations",
          "Participate in community health challenges for rewards",
          "Post questions, gratitude, or success stories",
          "Earn achievements and recognition for community participation",
          "Anonymous posting options for privacy"
        ]}
        tips={[
          "Join challenges to earn points and unlock rewards",
          "Use the gratitude category to thank those who helped you",
          "Anonymous posts still count toward your achievements"
        ]}
      />
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold" data-testid="text-page-title">Community Hub</h1>
          <p className="text-muted-foreground mt-1">
            Connect with others, share stories, and participate in challenges
          </p>
        </div>
        <Dialog open={isPostDialogOpen} onOpenChange={setIsPostDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="button-create-post">
              <Plus className="w-4 h-4 mr-2" />
              Share Story
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Share with the Community</DialogTitle>
              <DialogDescription>
                Your story can inspire others. All posts are reviewed before publishing.
              </DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit((data) => createPostMutation.mutate(data))} className="space-y-4">
                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Post Type</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-post-type">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {postTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              <div className="flex items-center gap-2">
                                <type.icon className={`w-4 h-4 ${type.color}`} />
                                {type.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Give your post a title" data-testid="input-post-title" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="content"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Content</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder="Share your story, ask a question, or start a discussion..."
                          className="min-h-[120px]"
                          data-testid="input-post-content"
                        />
                      </FormControl>
                      <FormDescription>
                        Do not share any sensitive personal health information
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="isAnonymous"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border p-4">
                      <div className="flex items-center gap-2">
                        {field.value ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        <div>
                          <FormLabel>Post Anonymously</FormLabel>
                          <FormDescription className="text-xs">
                            Your name won't be shown publicly
                          </FormDescription>
                        </div>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          data-testid="switch-anonymous"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {isAnonymous && (
                  <FormField
                    control={form.control}
                    name="authorDisplayName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Display Name (optional)</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="e.g., 'Hopeful Helper'"
                            data-testid="input-display-name"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={createPostMutation.isPending}
                  data-testid="button-submit-post"
                >
                  {createPostMutation.isPending ? "Submitting..." : "Submit for Review"}
                </Button>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-6 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 gap-2">
            <CardTitle className="text-sm font-medium">Stories Shared</CardTitle>
            <Trophy className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{successStories.length}</div>
            <p className="text-xs text-muted-foreground">Inspiring the community</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 gap-2">
            <CardTitle className="text-sm font-medium">Active Challenges</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeChallenges.length}</div>
            <p className="text-xs text-muted-foreground">Join and earn rewards</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 gap-2">
            <CardTitle className="text-sm font-medium">Achievements</CardTitle>
            <Award className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{achievements?.length || 0}</div>
            <p className="text-xs text-muted-foreground">Badges earned</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 gap-2">
            <CardTitle className="text-sm font-medium">Total Points</CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalPoints}</div>
            <p className="text-xs text-muted-foreground">Achievement points</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="stories">
        <TabsList>
          <TabsTrigger value="stories" data-testid="tab-stories">Success Stories</TabsTrigger>
          <TabsTrigger value="forum" data-testid="tab-forum">Forum</TabsTrigger>
          <TabsTrigger value="challenges" data-testid="tab-challenges">Challenges</TabsTrigger>
          <TabsTrigger value="achievements" data-testid="tab-achievements">My Achievements</TabsTrigger>
        </TabsList>

        <TabsContent value="stories" className="space-y-4">
          {postsLoading ? (
            <div className="grid gap-4 md:grid-cols-2">
              <Skeleton className="h-48" />
              <Skeleton className="h-48" />
            </div>
          ) : successStories.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2">
              {successStories.map((post) => (
                <Card key={post.id} className="overflow-hidden" data-testid={`post-card-${post.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
                        <Trophy className="w-3 h-3 mr-1" />
                        Success Story
                      </Badge>
                    </div>
                    <CardTitle className="text-lg">{post.title}</CardTitle>
                    <CardDescription className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className="text-xs">
                          {post.isAnonymous 
                            ? (post.authorDisplayName?.[0] || "A")
                            : (post.author?.firstName?.[0] || "U")
                          }
                        </AvatarFallback>
                      </Avatar>
                      <span>
                        {post.isAnonymous 
                          ? (post.authorDisplayName || "Anonymous")
                          : (post.author?.firstName || "User")
                        }
                      </span>
                      <span className="text-muted-foreground">
                        {format(new Date(post.createdAt), "MMM d, yyyy")}
                      </span>
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground line-clamp-3">{post.content}</p>
                  </CardContent>
                  <CardFooter className="flex items-center gap-4 pt-2 border-t">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => likePostMutation.mutate(post.id)}
                      data-testid={`button-like-${post.id}`}
                    >
                      <ThumbsUp className="w-4 h-4 mr-1" />
                      {post.likesCount}
                    </Button>
                    <Button variant="ghost" size="sm" data-testid={`button-comments-${post.id}`}>
                      <MessageCircle className="w-4 h-4 mr-1" />
                      {post.commentsCount}
                    </Button>
                    <span className="text-xs text-muted-foreground ml-auto">
                      <Eye className="w-3 h-3 inline mr-1" />
                      {post.viewsCount} views
                    </span>
                  </CardFooter>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="py-12">
              <CardContent className="text-center">
                <Trophy className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No success stories yet</p>
                <p className="text-sm text-muted-foreground mb-4">Be the first to share your journey!</p>
                <Button onClick={() => setIsPostDialogOpen(true)}>Share Your Story</Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="forum" className="space-y-4">
          {postsLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-24" />
              <Skeleton className="h-24" />
            </div>
          ) : forumPosts.length > 0 ? (
            <div className="space-y-4">
              {forumPosts.map((post) => {
                const typeInfo = postTypes.find(t => t.value === post.type);
                const Icon = typeInfo?.icon || MessageCircle;
                return (
                  <Card key={post.id} data-testid={`forum-post-${post.id}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="gap-1">
                            <Icon className={`w-3 h-3 ${typeInfo?.color}`} />
                            {typeInfo?.label}
                          </Badge>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(post.createdAt), "MMM d, yyyy")}
                        </span>
                      </div>
                      <CardTitle className="text-base">{post.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground line-clamp-2">{post.content}</p>
                    </CardContent>
                    <CardFooter className="flex items-center gap-4 pt-2 border-t">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => likePostMutation.mutate(post.id)}
                      >
                        <ThumbsUp className="w-4 h-4 mr-1" />
                        {post.likesCount}
                      </Button>
                      <Button variant="ghost" size="sm">
                        <MessageCircle className="w-4 h-4 mr-1" />
                        {post.commentsCount}
                      </Button>
                      <div className="flex items-center gap-2 ml-auto text-xs text-muted-foreground">
                        <Avatar className="h-5 w-5">
                          <AvatarFallback className="text-xs">
                            {post.isAnonymous ? "A" : post.author?.firstName?.[0] || "U"}
                          </AvatarFallback>
                        </Avatar>
                        {post.isAnonymous ? (post.authorDisplayName || "Anonymous") : post.author?.firstName}
                      </div>
                    </CardFooter>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className="py-12">
              <CardContent className="text-center">
                <MessageCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No forum posts yet</p>
                <p className="text-sm text-muted-foreground mb-4">Start a discussion with the community</p>
                <Button onClick={() => setIsPostDialogOpen(true)}>Create Post</Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="challenges" className="space-y-4">
          {challengesLoading ? (
            <div className="grid gap-4 md:grid-cols-2">
              <Skeleton className="h-56" />
              <Skeleton className="h-56" />
            </div>
          ) : activeChallenges.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2">
              {activeChallenges.map((challenge) => {
                const progress = myProgress?.find(p => p.challengeId === challenge.id);
                const progressPercent = progress 
                  ? (progress.currentValue / challenge.goalValue) * 100 
                  : 0;
                const isJoined = !!progress;
                const isCompleted = progress?.completed;

                return (
                  <Card key={challenge.id} className={isCompleted ? "border-green-500/50" : ""} data-testid={`challenge-card-${challenge.id}`}>
                    <CardHeader>
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant={isCompleted ? "default" : "outline"}>
                          {isCompleted ? (
                            <><CheckCircle className="w-3 h-3 mr-1" /> Completed</>
                          ) : isJoined ? (
                            <><Target className="w-3 h-3 mr-1" /> In Progress</>
                          ) : (
                            <><Sparkles className="w-3 h-3 mr-1" /> Available</>
                          )}
                        </Badge>
                        <Badge variant="secondary" className="gap-1">
                          <Users className="w-3 h-3" />
                          {challenge.currentParticipants || 0}
                        </Badge>
                      </div>
                      <CardTitle className="text-lg">{challenge.title}</CardTitle>
                      <CardDescription>{challenge.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">
                          <Calendar className="w-3 h-3 inline mr-1" />
                          Ends {format(new Date(challenge.endDate), "MMM d")}
                        </span>
                        <span className="font-medium text-green-600">
                          <Award className="w-3 h-3 inline mr-1" />
                          {formatCents(challenge.rewardAmount || 0)} reward
                        </span>
                      </div>
                      
                      {isJoined && (
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span>Progress</span>
                            <span className="font-medium">
                              {progress?.currentValue || 0} / {challenge.goalValue} {challenge.goalUnit || ""}
                            </span>
                          </div>
                          <Progress value={Math.min(progressPercent, 100)} className="h-2" />
                        </div>
                      )}
                    </CardContent>
                    <CardFooter>
                      {!isJoined ? (
                        <Button
                          className="w-full"
                          onClick={() => joinChallengeMutation.mutate(challenge.id)}
                          disabled={joinChallengeMutation.isPending}
                          data-testid={`button-join-${challenge.id}`}
                        >
                          <Target className="w-4 h-4 mr-2" />
                          Join Challenge
                        </Button>
                      ) : isCompleted ? (
                        <Button className="w-full" disabled variant="outline">
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Challenge Complete!
                        </Button>
                      ) : (
                        <Button className="w-full" variant="outline">
                          <TrendingUp className="w-4 h-4 mr-2" />
                          Log Progress
                        </Button>
                      )}
                    </CardFooter>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className="py-12">
              <CardContent className="text-center">
                <Target className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No active challenges</p>
                <p className="text-sm text-muted-foreground">Check back soon for new challenges!</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="achievements" className="space-y-4">
          {achievementsLoading ? (
            <div className="grid gap-4 md:grid-cols-3">
              <Skeleton className="h-40" />
              <Skeleton className="h-40" />
              <Skeleton className="h-40" />
            </div>
          ) : achievements && achievements.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-3">
              {achievements.map((userAchievement) => (
                <Card key={userAchievement.id} className="text-center" data-testid={`achievement-${userAchievement.id}`}>
                  <CardHeader>
                    <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                      <Award className="w-8 h-8 text-primary" />
                    </div>
                    <CardTitle className="text-base">{userAchievement.achievement.name}</CardTitle>
                    <CardDescription>{userAchievement.achievement.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-center gap-4">
                      <Badge variant="secondary">
                        <Star className="w-3 h-3 mr-1" />
                        {userAchievement.achievement.pointValue} pts
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        Earned {format(new Date(userAchievement.earnedAt), "MMM d, yyyy")}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="py-12">
              <CardContent className="text-center">
                <Award className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No achievements yet</p>
                <p className="text-sm text-muted-foreground">Complete actions and challenges to earn badges!</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
